// src/pages/ApiDocsStealthNet.jsx
import React, { useEffect, useMemo, useState } from "react";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import YAML from "yaml";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  GlobalStyles,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Card,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SecurityIcon from "@mui/icons-material/Security";
import LanIcon from "@mui/icons-material/Lan";
import PublicIcon from "@mui/icons-material/Public";
import ApiIcon from "@mui/icons-material/Api";
import LanguageIcon from "@mui/icons-material/Language";
import ShieldMoonIcon from "@mui/icons-material/ShieldMoon";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";

const BRAND = "#005CC5";

// Small helper: anchor section with consistent spacing
const Section = ({ id, title, subtitle, children, sx }) => (
  <Box id={id} sx={{ scrollMarginTop: 96, ...sx }}>
    <Typography variant="h4" fontWeight={800}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 900 }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ mt: 3 }}>{children}</Box>
  </Box>
);

// Simple code tabs for Quickstart (no external libs)
const CodeTabs = ({ examples }) => {
  const langs = Object.keys(examples);
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        {langs.map((l) => (
          <Tab key={l} label={l} />
        ))}
      </Tabs>
      <Box
        component="pre"
        sx={{
          p: 2,
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          background: (t) => (t.palette.mode === "dark" ? "#0d1117" : "#f8fafc"),
          overflow: "auto",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 13.5,
        }}
      >
        {examples[langs[tab]]}
      </Box>
    </Box>
  );
};

export default function ApiDocsStealthNet() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // left rail sticky
  const toc = [
    { id: "about", label: "About StealthNet AI" },
    { id: "agents", label: "AI Agents" },
    { id: "quickstart", label: "Quickstart" },
    { id: "reference", label: "API Reference" },
  ];

  // swagger spec state
  const [raw, setRaw] = useState("");
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [apiKey, setApiKey] = useState(localStorage.getItem("api_key") || "");
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [tab, setTab] = useState(0); // Reference/Guides tabs

  // fetch swagger.yaml from /public
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/swagger.yaml", { cache: "no-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status} fetching /swagger.yaml`);
        const txt = (await res.text()).replace(/^\uFEFF/, "");
        setRaw(txt);
        const parsed = YAML.parseDocument(txt, { strict: true });
        if (parsed.errors?.length) {
          setErr({ type: "yaml", errors: parsed.errors });
        } else {
          setDoc(parsed);
          const json = parsed.toJSON();
          const list = json?.servers?.map((s) => s.url) || [];
          setServers(list);
          if (list.length) setSelectedServer(list[0]);
        }
      } catch (e) {
        setErr({ type: "fetch", message: String(e.message || e) });
      }
    })();
  }, []);

  const spec = useMemo(() => {
    if (!doc || err?.type === "yaml") return null;
    const json = doc.toJSON();
    const ver = json?.openapi || json?.swagger;
    const ok =
      (typeof json?.openapi === "string" && /^3\.\d+\.\d+$/.test(json.openapi)) ||
      json?.swagger === "2.0";
    if (!ok) {
      setErr({ type: "version", message: `Invalid version header: ${String(ver)}` });
      return null;
    }
    return json;
  }, [doc, err]);




  // brand overrides for Swagger UI
  const GlobalBrand = (
    <GlobalStyles
      styles={{
        ":root": { "--brand": BRAND },
        ".swagger-ui": { fontFamily: theme.typography.fontFamily },
        ".swagger-ui .topbar": { display: "none" },
        ".swagger-ui .info .title": { fontWeight: 900, letterSpacing: "-0.01em" },
        ".swagger-ui .btn, .swagger-ui .opblock-control__btn": { borderRadius: 10 },
        ".swagger-ui .btn.authorize": {
          background: "var(--brand) !important",
          borderColor: "var(--brand) !important",
          color: "#fff !important",
        },
        ".swagger-ui .opblock": {
          background: isDark ? theme.palette.background.paper : "#fff",
          borderRadius: 12,
          borderColor: theme.palette.divider,
        },
        ".swagger-ui .opblock-tag": { fontWeight: 700, fontSize: "1.06rem" },
        ".swagger-ui .opblock-summary-method": {
          background: "var(--brand) !important",
          color: "#fff !important",
          borderRadius: "8px 0 0 8px",
          padding: "6px 10px",
          minWidth: 70,
          textAlign: "center",
        },
        ".swagger-ui .copy-to-clipboard": { borderRadius: 8 },
        ".swagger-ui .response-col_links": { display: "none" },
        ".swagger-ui .model-title, .swagger-ui .model tr, .swagger-ui table": {
          color: theme.palette.text.primary,
        },
      }}
    />
  );

  // quickstart code samples (login -> token -> GET /pentests)
  const samples = {
    curl: `# 1) Login and capture token (requires jq)
TOKEN=$(curl -sS -X POST https://api.stealthnet.ai/user/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"user@example.com","password":"P@ssw0rd!"}' | jq -r '.token')

# 2) Use the token to list pentests
curl -sS -H "Authorization: Bearer $TOKEN" \\
  https://api.stealthnet.ai/pentests`,
    "Node.js": `// Node 18+ (fetch is built-in)
const base = "https://api.stealthnet.ai";

async function main() {
  // 1) Login -> get token
  const login = await fetch(base + "/user/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: "user@example.com", password: "P@ssw0rd!" }),
  });
  const loginJson = await login.json();
  const token = loginJson.token;

  // 2) Use token -> list pentests
  const res = await fetch(base + "/pentests", {
    headers: { Authorization: \`Bearer \${token}\` },
  });
  console.log(await res.json());
}
main().catch(console.error);`,
    Python: `import requests

BASE = "https://api.stealthnet.ai"

# 1) Login -> token
login = requests.post(
    f"{BASE}/user/login",
    json={"email": "user@example.com", "password": "P@ssw0rd!"},
)
login.raise_for_status()
token = login.json()["token"]

# 2) Use token -> list pentests
res = requests.get(f"{BASE}/pentests", headers={"Authorization": f"Bearer {token}"})
print(res.json())`,
    Go: `package main

import (
  "bytes"
  "encoding/json"
  "fmt"
  "io"
  "net/http"
)

func main() {
  base := "https://api.stealthnet.ai"

  // 1) Login -> token
  loginPayload := map[string]string{"email": "user@example.com", "password": "P@ssw0rd!"}
  lp, _ := json.Marshal(loginPayload)
  req, _ := http.NewRequest("POST", base+"/user/login", bytes.NewReader(lp))
  req.Header.Set("Content-Type", "application/json")
  resp, _ := http.DefaultClient.Do(req)
  defer resp.Body.Close()
  var loginResp struct{ Token string \`json:"token"\` }
  json.NewDecoder(resp.Body).Decode(&loginResp)

  // 2) Use token -> list pentests
  req2, _ := http.NewRequest("GET", base+"/pentests", nil)
  req2.Header.Set("Authorization", "Bearer "+loginResp.Token)
  resp2, _ := http.DefaultClient.Do(req2)
  body, _ := io.ReadAll(resp2.Body)
  fmt.Println(string(body))
}`,
  };

  return (
    <>
      {GlobalBrand}

      {/* Header Card (gradient) */}
      <Container sx={{ pt: 6 }}>
        <Card
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${BRAND} 0%, #003a88 100%)`,
            color: "white",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
                API Documentation
              </Typography>
              <Typography sx={{ opacity: 0.9, mt: 1 }}>
                Explore endpoints, authenticate, and run requests inline.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: "wrap" }}>
                <Chip
                  icon={<LanIcon sx={{ color: "#fff" }} />}
                  label={`Servers: ${servers.length || 0}`}
                  variant="outlined"
                  sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
                />
                <Chip
                  icon={<SecurityIcon sx={{ color: "#fff" }} />}
                  label="Auth: JWT"
                  variant="outlined"
                  sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
                />
                <Chip
                  icon={<RocketLaunchIcon sx={{ color: "#fff" }} />}
                  label="Try it out"
                  variant="outlined"
                  sx={{ borderColor: alpha("#fff", 0.6), color: "white", "& .MuiChip-icon": { color: "#fff" } }}
                />
              </Stack>
            </Box>
            <Button
              onClick={() => document.getElementById("reference")?.scrollIntoView({ behavior: "smooth" })}
              variant="contained"
              endIcon={<OpenInNewIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 2.5,
                py: 1.25,
                bgcolor: "white",
                color: BRAND,
                "&:hover": { bgcolor: alpha("#fff", 0.9) },
              }}
            >
              Open Reference
            </Button>
          </Stack>
        </Card>
      </Container>

      {/* Content */}
      <Container maxWidth="lg" sx={{ pb: 5 }}>
        <Grid container spacing={4}>
          {/* Sticky left nav */}
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: { md: "sticky" },
                top: 100,
                p: 2,
                borderRadius: 2,
                border: (t) => `1px solid ${t.palette.divider}`,
              }}
            >
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                On this page
              </Typography>
              <Box component="nav" sx={{ mt: 1.5, display: "grid", gap: 0.75 }}>
                {toc.map((t) => (
                  <Button
                    key={t.id}
                    onClick={() => {
                      document.getElementById(t.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    size="small"
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      color: "text.primary",
                      "&:hover": { bgcolor: (th) => th.palette.action.hover },
                    }}
                  >
                    {t.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Main */}
          <Grid item xs={12} md={9}>
            {/* About */}
            <Section
              id="about"
              title="About StealthNet AI"
              subtitle="StealthNet AI automates the grind of pentesting so your team can focus on strategy and remediation. Our agents enumerate assets, map attack surfaces, execute targeted scans, synthesize findings, and export compliance-ready reports."
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: (t) => (t.palette.mode === "dark" ? "#0b1320" : "#f6f9ff"),
                  borderColor: (t) => (t.palette.mode === "dark" ? "#11223c" : "#dbe7ff"),
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" fontWeight={800}>
                      Platform Highlights
                    </Typography>
                    <Box component="ul" sx={{ mt: 1.5, pl: 3 }}>
                      <li>Fleet of specialized AI agents orchestrated for External, API, Web, Vishing, and Dark Web scenarios.</li>
                      <li>Actionable output: deduped findings, risk scoring, and step-by-step remediation tips.</li>
                      <li>Developer-first API: JWT auth, idempotent operations, and structured JSON schemas.</li>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: (t) => `1px dashed ${t.palette.divider}`,
                        background: (t) => (t.palette.mode === "dark" ? "#0e1729" : "#fff"),
                      }}
                    >
                      <Typography variant="overline" sx={{ opacity: 0.8 }}>
                        Compliance
                      </Typography>
                      <Typography variant="body2">
                        SOC 2, PCI-DSS, and HIPAA-friendly workflows with exportable evidence and executive summaries.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Section>

            {/* Agents */}
            <Section
              id="agents"
              title="AI Agents"
              subtitle="Each agent has a focused job. Use them alone, or combine for full-stack coverage."
              sx={{ mt: 6 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ bgcolor: BRAND }}>
                        <LanIcon htmlColor="#fff" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={800}>
                        External
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Discovers assets, subdomains, open ports, tech stacks; orchestrates scans and correlates exploits/CVEs.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip size="small" label="Recon" sx={{ mr: 1 }} />
                      <Chip size="small" label="Service Fingerprinting" sx={{ mr: 1 }} />
                      <Chip size="small" label="Exploit Mapping" />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ bgcolor: BRAND }}>
                        <PublicIcon htmlColor="#fff" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={800}>
                        Dark Web Monitoring
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Tracks leaked creds, breach chatter, and exposure intel tied to your domains and identities.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip size="small" label="Credential Leaks" sx={{ mr: 1 }} />
                      <Chip size="small" label="Breach Intel" sx={{ mr: 1 }} />
                      <Chip size="small" label="Auto-Reports" />
                    </Box>
                  </Paper>
                </Grid>

                {/* Vishing card (added) */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ bgcolor: BRAND }}>
                        <PhoneInTalkIcon htmlColor="#fff" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={800}>
                        Vishing
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Simulates real-world voice phishing: calls targets, adapts scripts, logs transcripts, and scores risk.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip size="small" label="Voice Scenarios" sx={{ mr: 1 }} />
                      <Chip size="small" label="Auto-Transcripts" sx={{ mr: 1 }} />
                      <Chip size="small" label="Risk Scoring" />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ bgcolor: BRAND }}>
                        <ApiIcon htmlColor="#fff" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={800}>
                        API
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Consumes OpenAPI/Swagger or live endpoints; fuzzes params, auth flows, and discovers shadow routes.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip size="small" label="Schema-Aware" sx={{ mr: 1 }} />
                      <Chip size="small" label="Auth Probing" sx={{ mr: 1 }} />
                      <Chip size="small" label="OWASP API Top 10" />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ bgcolor: BRAND }}>
                        <LanguageIcon htmlColor="#fff" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={800}>
                        Web
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      Crawls apps, detects flows (auth/checkout/user), tests common vulns and risky configs with agent loops.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip size="small" label="Crawler" sx={{ mr: 1 }} />
                      <Chip size="small" label="Session-Aware" sx={{ mr: 1 }} />
                      <Chip size="small" label="OWASP Top 10" />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: (t) => `2px dashed ${t.palette.divider}`,
                      background: (t) => (t.palette.mode === "dark" ? "#0e1729" : "#fff"),
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ bgcolor: BRAND }}>
                        <ShieldMoonIcon htmlColor="#fff" />
                      </Avatar>
                      <Typography variant="h6" fontWeight={800}>
                        Internal <Chip label="Coming Soon" size="small" sx={{ ml: 1, bgcolor: BRAND, color: "#fff" }} />
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                      AD discovery, lateral movement simulations, misconfig sweeps, and credential hygiene at scale.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Section>

            {/* Quickstart */}
            <Section
              id="quickstart"
              title="Quickstart"
              subtitle="Login to obtain a JWT, then list pentests using the token."
              sx={{ mt: 6 }}
            >
              

              <Box sx={{ mt: 3 }}>
                <CodeTabs examples={samples} />
              </Box>
            </Section>

            {/* Reference */}
            <Section
              id="reference"
              title="API Reference"
              subtitle="Search, expand, and run requests inline. ‘Try it out’ sends requests from your browser."
              sx={{ mt: 6 }}
            >
              <Paper
                variant="outlined"
                sx={{
                  mt: 1,
                  borderRadius: 3,
                  overflow: "hidden",
                  "& .swagger-ui": {
                    background: isDark ? theme.palette.background.default : "#fff",
                  },
                }}
              >
                {err?.type === "fetch" && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body1" color="error.main">
                      Can’t load swagger.yaml — {err.message}
                    </Typography>
                  </Box>
                )}
                {err?.type === "yaml" && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body1" color="error.main" gutterBottom>
                      YAML parse errors
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 3, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }}
                    >
                      {err.errors.map((e, i) => (
                        <li key={i}>
                          line {e.line + 1}, col {e.col + 1}: {e.message}
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}
                {!spec && !err ? (
                  <Box sx={{ p: 2, fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {raw ? "Parsing spec…" : "Loading spec…"}
                  </Box>
                ) : spec ? (
                  <SwaggerUI
                    spec={spec}
                    tryItOutEnabled
                    persistAuthorization
                    displayRequestDuration
                    docExpansion="list"
                    defaultModelsExpandDepth={1}
                    defaultModelExpandDepth={2}
                    
                  />
                ) : null}
              </Paper>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  variant="text"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                  Back to top
                </Button>
              </Box>
            </Section>

            {/* Footer CTA */}
            <Box sx={{ mt: 8, mb: 4 }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  background: (t) => (t.palette.mode === "dark" ? "#0b1320" : "#f6f9ff"),
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md>
                    <Typography variant="h6" fontWeight={900}>
                      Ready to automate your pentest workflow?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Spin up an agent, run your first scan, and export a report in minutes.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md="auto">
                    <Button
                      variant="contained"
                      size="large"
                      href="/signup"
                      sx={{ bgcolor: BRAND, "&:hover": { bgcolor: "#014fa9" } }}
                    >
                      Get API Access
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
